import * as React from 'react';
import cx from "classnames";

import './field-container.styles.scss';
import { useFieldValidation } from '../../context';
import { trDts } from '@modules/datasheets/utils/i18n';

export type FieldContainerProps = React.PropsWithChildren<{
  width: number;
  height: number;
  style?: React.CSSProperties;
  showBorder?: boolean;
  className?: string;
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
  fieldId?: number;
  hasValidationError?: boolean;
  required?: boolean;
  isEmpty?: boolean;
}>;

export const FieldContainer = React.forwardRef<HTMLDivElement, FieldContainerProps>((props, ref) => {
  const { children, style, className, width, height, showBorder, onKeyDown, fieldId, hasValidationError, required, isEmpty } = props;
  const { fieldErrors, clearFieldError } = useFieldValidation();

  const requiredEmpty = required === true && isEmpty === true;
  const hasError = hasValidationError || (fieldId != null && fieldId in fieldErrors) || requiredEmpty;

  const errorMessage = React.useMemo(() => {
    if (requiredEmpty) return trDts('This field is required');
    if (fieldId != null && fieldErrors[fieldId]) return fieldErrors[fieldId];
    return undefined;
  }, [requiredEmpty, fieldId, fieldErrors]);

  const handleFocus = React.useCallback(() => {
    if (fieldId != null && fieldId in fieldErrors) {
      clearFieldError(fieldId);
    }
  }, [fieldId, fieldErrors, clearFieldError]);

  return (
    <div
      ref={ref}
      onKeyDown={onKeyDown}
      onFocus={handleFocus}
      title={errorMessage}
      style={{
        width: typeof width === 'number' ? `${width}px` : width, 
        height: typeof height === 'number' ? `${height}px` : height,
        ...style,
      }}
      className={cx(
        'DatasheetsResultFieldContainer', 
        { 
          'DatasheetsResultFieldContainer--no-border': showBorder === false,
          'DatasheetsResultFieldContainer--validation-error': hasError,
        },
        className
      )}
    >
      {children}
    </div>
  );
});

FieldContainer.displayName = 'FieldContainer';